import { SearchableJobSortField } from 'jobs/dtos/job'
import SearchableJob from 'jobs/SearchableJob'
import { Resolution } from 'jobs/Job'
import { JobProgressionStepStatus } from 'jobs/progression/JobProgression'
import { JobScopeChangeStatus } from '@taxfyle/api-internal/internal/job_scope_change_pb'

/**
 * Column (row?) density presets.
 */
export enum ColumnDensity {
  Default,
  Comfortable,
  Compact,
}

/**
 * Unique identifiers for each column.
 */
export enum ColumnId {
  Checkbox = 'checkbox',
  JobTitle = 'jobTitle',
  JobId = 'jobId',
  JobShortId = 'jobShortId',
  LegendName = 'legendName',
  Workspace = 'workspace',
  Status = 'status',
  ProgressionStep = 'progressionStep',
  JobScopeChangeRequestStatus = 'jobScopeChangeRequestStatus',
  ClientTeam = 'clientTeam',
  ClientChampion = 'clientChampion',
  ClientChampionEmail = 'clientChampionEmail',
  ProChampion = 'proChampion',
  ProChampionEmail = 'proChampionEmail',
  IsRequestedPro = 'isRequestedPro',
  Payout = 'payout',
  Total = 'total',
  TotalPaid = 'totalPaid',
  Created = 'created',
  Submitted = 'submitted',
  Claimed = 'claimed',
  Completed = 'completed',
  Action = 'action',
  LastProMessageActivity = 'lastProMessageActivity',
  LastClientMessageActivity = 'lastClientMessageActivity',
  LastRelationshipManagerMessageActivity = 'lastRelationshipManagerMessageActivity',
}

/**
 * Whether a column is common to all users or particular to clients, providers, or admins.
 * Admin users can view all column types.
 */
export enum ColumnType {
  Common = 'common',
  Client = 'client',
  Provider = 'provider',
  Admin = 'admin',
}

export interface StaticColumnDetails {
  id: ColumnId
  header: string
  modifyVisibility: boolean
  field?: SearchableJobSortField
  sortable: boolean

  /**
   * Indicates whether the column should be pinned as the first column because it's a checkbox action selector.
   * This makes it immobile in the column manager.
   */
  actionSelector?: boolean
}

export interface MutableColumnDetails {
  id: string
  visible: boolean
}

/**
 * Static column details for populating the Job Management table.
 */
export const JobsTableColumns: StaticColumnDetails[] = [
  {
    id: ColumnId.Checkbox,
    header: '',
    modifyVisibility: true,
    sortable: false,
    actionSelector: true,
  },
  {
    id: ColumnId.JobTitle,
    header: 'Job title',
    modifyVisibility: false,
    field: 'Title',
    sortable: true,
  },
  {
    id: ColumnId.JobShortId,
    header: 'Job ID',
    modifyVisibility: true,
    sortable: false,
  },
  {
    id: ColumnId.LegendName,
    header: 'Job type',
    modifyVisibility: true,
    field: 'LegendName',
    sortable: true,
  },
  {
    id: ColumnId.Workspace,
    header: 'Workspace',
    modifyVisibility: true,
    field: 'WorkspaceName',
    sortable: true,
  },
  {
    id: ColumnId.Status,
    header: 'Status',
    modifyVisibility: true,
    sortable: false,
  },
  {
    id: ColumnId.ProgressionStep,
    header: 'Job progress',
    modifyVisibility: true,
    sortable: false,
  },
  {
    id: ColumnId.JobScopeChangeRequestStatus,
    header: 'Job scope updates',
    modifyVisibility: true,
    sortable: false,
  },
  {
    id: ColumnId.ClientTeam,
    header: 'Client team',
    modifyVisibility: true,
    field: 'ClientTeamName',
    sortable: true,
  },
  {
    id: ColumnId.ClientChampion,
    header: 'Client champion',
    modifyVisibility: true,
    field: 'ClientChampionName',
    sortable: true,
  },
  {
    id: ColumnId.ClientChampionEmail,
    header: 'Client champion email',
    modifyVisibility: true,
    field: 'ClientChampionEmail',
    sortable: true,
  },
  {
    id: ColumnId.ProChampion,
    header: 'Pro champion',
    modifyVisibility: true,
    field: 'ProChampionName',
    sortable: true,
  },
  {
    id: ColumnId.ProChampionEmail,
    header: 'Pro champion email',
    modifyVisibility: true,
    field: 'ProChampionEmail',
    sortable: true,
  },
  {
    id: ColumnId.IsRequestedPro,
    header: 'Is requested pro',
    modifyVisibility: true,
    sortable: false,
  },
  {
    id: ColumnId.Payout,
    header: 'Payout',
    modifyVisibility: true,
    field: 'ProviderCutAmount',
    sortable: true,
  },
  {
    id: ColumnId.Total,
    header: 'Total',
    modifyVisibility: true,
    sortable: false,
  },
  {
    id: ColumnId.TotalPaid,
    header: 'Total paid',
    modifyVisibility: true,
    sortable: false,
  },
  {
    id: ColumnId.Created,
    header: 'Created',
    modifyVisibility: true,
    field: 'DateCreated',
    sortable: true,
  },
  {
    id: ColumnId.Submitted,
    header: 'Submitted',
    modifyVisibility: true,
    field: 'DateSubmitted',
    sortable: true,
  },
  {
    id: ColumnId.Claimed,
    header: 'Claimed',
    modifyVisibility: true,
    field: 'DateClaimed',
    sortable: true,
  },
  {
    id: ColumnId.Completed,
    header: 'Completed',
    modifyVisibility: true,
    field: 'DateCompleted',
    sortable: true,
  },
  {
    id: ColumnId.LastProMessageActivity,
    header: 'Last pro message',
    modifyVisibility: true,
    field: 'DateLastProMessageActivity',
    sortable: true,
  },
  {
    id: ColumnId.LastClientMessageActivity,
    header: 'Last client message',
    modifyVisibility: true,
    field: 'DateLastClientMessageActivity',
    sortable: true,
  },
  {
    id: ColumnId.LastRelationshipManagerMessageActivity,
    header: 'Last RM message',
    modifyVisibility: true,
    field: 'DateLastRelationshipManagerMessageActivity',
    sortable: true,
  },
]

/**
 * Returns the exported value for a jobs table column.
 */
export function exportValue(columnId: ColumnId, job: SearchableJob) {
  switch (columnId) {
    case ColumnId.JobTitle:
      return job.displayName
    case ColumnId.JobId:
      return job.id
    case ColumnId.JobShortId:
      return job.shortId
    case ColumnId.LegendName:
      return job.legendName
    case ColumnId.Workspace:
      return job.workspace.name
    case ColumnId.Status:
      return formattedStatus(job)
    case ColumnId.ProgressionStep:
      return formattedProgressionStep(job)
    case ColumnId.JobScopeChangeRequestStatus:
      return formattedJobScopeChangeRequestStatus(job.scopeChangeStatus)
    case ColumnId.Payout:
      return job.formattedProviderCutAmount
    case ColumnId.Total:
      return job.formattedTotalPrice
    case ColumnId.ClientTeam:
      return job.clientTeamName
    case ColumnId.ClientChampion:
      return job.championClient?.displayName
    case ColumnId.ClientChampionEmail:
      return job.championClient?.email ?? undefined
    case ColumnId.ProChampion:
      return job.championPro?.displayName
    case ColumnId.ProChampionEmail:
      return job.championPro?.email ?? undefined
    case ColumnId.Created:
      return formattedDate(job.dateCreated)
    case ColumnId.Submitted:
      return formattedDate(job.dateSubmitted)
    case ColumnId.Claimed:
      return formattedDate(job.dateAccepted)
    case ColumnId.Completed:
      return formattedDate(job.dateClosed)
    case ColumnId.LastProMessageActivity:
      return formattedDate(job.dateLastProMessageActivity)
    case ColumnId.LastClientMessageActivity:
      return formattedDate(job.dateLastClientMessageActivity)
    case ColumnId.LastRelationshipManagerMessageActivity:
      return formattedDate(job.dateLastRelationshipManagerMessageActivity)
    case ColumnId.IsRequestedPro:
      return formattedBoolean(job.isRequestedPro)
    default:
      return undefined
  }
}

/**
 * Returns the formatted status.
 */
function formattedStatus(job: SearchableJob) {
  const withResolution = () => {
    switch (job.resolution) {
      case Resolution.Cancelled:
        return 'Closed (cancelled)'
      case Resolution.Test:
        return 'Closed (test)'
      default:
        return 'Closed'
    }
  }

  switch (job.status) {
    case 'UNDER_CONSTRUCTION':
      return 'Onboarding'
    case 'INFO_GATHERING':
      return 'Info Gathering'
    case 'UNCLAIMED':
      return 'Unclaimed'
    case 'CLAIMED':
      return 'In Progress'
    case 'CLOSED':
      return withResolution()
    case 'IDLE':
      return 'Idle'
    case 'ON_HOLD':
      return 'On Hold'
  }
}

/**
 * Returns the formatted progression step.
 */
export function formattedProgressionStep(job: SearchableJob) {
  switch (job.progressionStep) {
    case JobProgressionStepStatus.OPEN_ITEMS:
      return 'Open items'
    case JobProgressionStepStatus.JOB_STARTED:
      return 'Job started'
    case JobProgressionStepStatus.DRAFT_IN_REVIEW:
      return 'Draft review'
    case JobProgressionStepStatus.DRAFT_APPROVED:
      return 'Draft approved'
    case JobProgressionStepStatus.DRAFT_REJECTED:
      return 'Changes requested'
    case JobProgressionStepStatus.AUTHORIZATION_REQUESTED:
      return 'Authorization requested'
    case JobProgressionStepStatus.AUTHORIZATION_SIGNED:
      return 'Authorization received'
    case JobProgressionStepStatus.RETURN_FILED:
      return 'Return filed'
    case JobProgressionStepStatus.PROGRESSION_STEP_UNSPECIFIED:
      return ''
    case JobProgressionStepStatus.NOT_STARTED:
      return 'Review documents'
    case JobProgressionStepStatus.EXTENSION_IN_PROGRESS:
      return 'Extension in progress'
    case JobProgressionStepStatus.EXTENSION_CONFIRMED:
      return 'Extension confirmed'
    case JobProgressionStepStatus.EXTENSION_ACCEPTED:
      return 'Extension accepted'
    case JobProgressionStepStatus.EXTENSION_REJECTED:
      return 'Extension rejected'
    case JobProgressionStepStatus.READY_TO_FILE:
      return 'Ready to file'
    case JobProgressionStepStatus.PROGRESSION_WAITING_ON_CLIENT:
      return 'Waiting on client'
    case JobProgressionStepStatus.DELIVERABLE_COMPLETED:
      return 'Deliverable completed'
    case JobProgressionStepStatus.REVIEW_COMPLETED:
      return 'Review completed'
    case JobProgressionStepStatus.EXTENSION_NOT_STARTED:
      return 'Extension not started'
    case JobProgressionStepStatus.PREPARATION_COMPLETED:
      return 'Preparation completed'
    default:
      return ''
  }
}

/**
 * Returns a formatted date.
 */
function formattedDate(date: Date | null) {
  return date?.toLocaleString()
}

export function formattedJobScopeChangeRequestStatus(
  scopeChangeRequestStatus: JobScopeChangeStatus,
) {
  switch (scopeChangeRequestStatus) {
    case JobScopeChangeStatus.UNSPECIFIED:
      return undefined
    case JobScopeChangeStatus.ACCEPTED:
      return 'Accepted'
    case JobScopeChangeStatus.REJECTED:
    case JobScopeChangeStatus.APPLY_FAILED:
      return 'Rejected'
    case JobScopeChangeStatus.PENDING_ADMIN_APPROVAL:
    case JobScopeChangeStatus.PENDING_FINALIZATION:
    case JobScopeChangeStatus.PENDING_CLIENT_APPROVAL:
      return 'Pending review'
    default:
      return undefined
  }
}

/**
 * Returns a formatted boolean value.
 */
function formattedBoolean(value?: boolean) {
  return value ? 'Yes' : 'No'
}
